import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useImagePalette, usePaletteSettings } from './hooks';
import { formatColor, isLight, type ColorFormat, type Swatch } from './palette';

const COPIED_MS = 1200;

interface ColorSwatchesProps {
  swatches: Swatch[];
  format: ColorFormat;
  /** Circle diameter in px. */
  size?: number;
  className?: string;
}

/** A row of color circles; clicking one copies its code to the clipboard. */
export function ColorSwatches({ swatches, format, size = 26, className }: ColorSwatchesProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async (swatch: Swatch) => {
    const code = formatColor(swatch, format);
    try {
      await navigator.clipboard.writeText(code);
      setCopied(swatch.hex);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), COPIED_MS);
      toast.success(`Copied ${code}`, { duration: 1500 });
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  return (
    <div className={cn('flex items-center gap-1.5', className)} role="list" aria-label="Color palette">
      {swatches.map((swatch) => {
        const code = formatColor(swatch, format);
        const light = isLight(swatch.rgb);
        return (
          <button
            key={swatch.hex}
            type="button"
            role="listitem"
            onClick={(e) => {
              e.stopPropagation();
              void copy(swatch);
            }}
            title={`Copy ${code}`}
            aria-label={`Copy ${code}`}
            className={cn(
              'rounded-full flex items-center justify-center shrink-0 transition-transform duration-150 hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              light && 'ring-1 ring-inset ring-black/10',
            )}
            style={{ width: size, height: size, backgroundColor: swatch.hex }}
          >
            {copied === swatch.hex && (
              <Check className={cn('w-3.5 h-3.5', light ? 'text-black/70' : 'text-white')} strokeWidth={3} />
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * An image's palette as a row of circles, or nothing while the extension is
 * off or the image can't be read. Mount it only where it is visible (e.g.
 * inside dialog content), since it reads the image.
 */
export function ImagePalette({ url, size }: { url: string; size?: number }) {
  const { enabled, colors, format } = usePaletteSettings();
  const swatches = useImagePalette(url, enabled);
  if (!enabled || !swatches) return null;
  return <ColorSwatches swatches={swatches.slice(0, colors)} format={format} size={size} />;
}
